import { useEffect, useMemo, useRef, useState } from 'react';
import { TRACKS } from '../constants/appConstants.js';
import {
  getLearnerState, getLesson, getFirstLesson, getNextLesson,
  getLessonProgress, logQuizAttempt, getTrackReadiness,
  markLessonComplete, upsertLessonProgress, upsertLearnerState,
  buildStatePayload,
} from '../services/supabaseClient.js';
import { callAI } from '../services/aiService.js';
import { parseResponse } from '../utils/parser.js';
import QuizPanel from './QuizPanel.jsx';

// ─────────────────────────────────────────────────────────────────
// TestOutTool — Dashboard → Assessments → Test Out of This Lesson.
//
// A governed mastery shortcut: prove the current lesson (or a range)
// in a single formal checkpoint. Pass ≥80% on the FIRST attempt
// advances current_lesson_id past the selected range and marks each
// lesson complete (source = test_out, logged via the
// quiz_attempts.remediation_strategy free-text column). Fail records
// mastery_level='test_out_ineligible' on each selected lesson so this
// tool will not offer the tier for that range again — the learner
// continues normally through Study Console.
//
// VISUAL: new AdaptOS design system (white cards, amber CTA, emerald
// pass states, coral gaps, slate metadata, dark diagnostic panel).
// SCORING + ADVANCEMENT LOGIC IS UNCHANGED from the prior Test Out.
//
// No schema change. Engine is NOT instantiated here (same rationale as
// VideoLessonTool — avoids cross-session state collisions). The retry
// gate is per-lesson, persisted in lesson_progress.mastery_level so
// existing readers (fetchRoadmapProgress, engine, dashboard) ignore the
// sentinel value.
// ─────────────────────────────────────────────────────────────────

const PASS_THRESHOLD = 0.80;
const TEST_OUT_INELIGIBLE = 'test_out_ineligible';

const COPY = {
  pass: 'Fast Track cleared. AdaptOS advanced you to the next concept.',
  fail: 'Not cleared this time. AdaptOS is routing you through the guided path — the missed areas are now repair targets.',
  unavailable: 'Fast Track Assessment is temporarily unavailable. Continue through Study Console.',
};

// Tier icons ──────────────────────────────────────────────────────
function IcShield() {
  return (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>);
}
function IcZap() {
  return (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>);
}
function IcTrophy() {
  return (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></svg>);
}

// Three named tiers — keys stay 'A'/'B'/'C' so the eligibility / slice
// logic is untouched; copy + counts match the reference mock.
const TIERS = [
  { key: 'A', name: 'Advance', Icon: IcShield, tint: 'var(--brand-emerald)', best: 'Best if you want to prove the current lesson only.',               scope: 'Current lesson',           lessonCount: 1, questionCount: 10 },
  { key: 'B', name: 'Excel',   Icon: IcZap,    tint: 'var(--brand-amber)',   best: 'Best if the material feels familiar and you want to move faster.', scope: 'Current + next lesson',    lessonCount: 2, questionCount: 20 },
  { key: 'C', name: 'Master',  Icon: IcTrophy, tint: '#7c3aed',              best: 'Best for strong students moving through modules quickly.',         scope: 'Current + next 2 lessons', lessonCount: 3, questionCount: 30 },
];

export default function TestOutTool({ learner, onSelectTrack }) {
  const trackId = learner?.current_track || null;
  const track   = useMemo(() => (trackId ? TRACKS[trackId] : null), [trackId]);

  // Boot state ───────────────────────────────────────────────────
  const [bootError,     setBootError]     = useState(null);
  const [lessons,       setLessons]       = useState([]);  // [current, next, next+1] (some may be null)
  const [progressByLid, setProgressByLid] = useState({});  // lessonId -> lesson_progress row or null
  const [weakSignal,    setWeakSignal]    = useState(null); // { errorType, lastResult } from learner_state
  const [readiness,     setReadiness]     = useState(null); // { pct } from getTrackReadiness

  // UX state ─────────────────────────────────────────────────────
  // phase: 'select' → 'quiz' → 'passed' | 'failed' | 'unavailable'
  const [phase,         setPhase]         = useState('select');
  const [selectedOpt,   setSelectedOpt]   = useState(null);  // 'A' | 'B' | 'C'
  const [quiz,          setQuiz]          = useState(null);
  const [quizLoading,   setQuizLoading]   = useState(false);
  const [quizError,     setQuizError]     = useState(null);

  // Hard guard against double-submit on QuizPanel onComplete.
  const persistingRef = useRef(false);

  // ── Load up to three lessons + their progress rows ───────────
  useEffect(() => {
    if (!learner?.id) { setBootError('Sign in to use Fast Track Assessment.'); return; }
    if (!trackId)     { setBootError('Pick a track on the Dashboard first.'); return; }

    let cancelled = false;
    (async () => {
      try {
        const state = await getLearnerState(learner.id, trackId);
        if (!cancelled && state) {
          setWeakSignal({ errorType: state.last_error_type || null, lastResult: state.last_checkpoint_result || null });
        }
        getTrackReadiness(learner.id, trackId)
          .then(r => { if (!cancelled && r) setReadiness(r); })
          .catch(() => {});

        let cur = null;
        if (state?.current_lesson_id) {
          const candidate = await getLesson(state.current_lesson_id);
          if (candidate?.modules?.track_id === trackId) cur = candidate;
        }
        if (!cur) cur = await getFirstLesson(trackId);
        if (!cur) { if (!cancelled) setBootError('No lessons available on this track yet.'); return; }

        let nxt = null;
        try { nxt = await getNextLesson(cur.id); } catch { nxt = null; }
        let nxt2 = null;
        if (nxt) {
          try { nxt2 = await getNextLesson(nxt.id); } catch { nxt2 = null; }
        }

        const triple = [cur, nxt, nxt2];
        // Fetch progress rows in parallel (null when no row exists).
        const progressRows = await Promise.all(
          triple.map(l => l ? getLessonProgress(learner.id, l.id) : Promise.resolve(null))
        );
        if (cancelled) return;
        setLessons(triple);
        const map = {};
        triple.forEach((l, i) => { if (l) map[l.id] = progressRows[i] || null; });
        setProgressByLid(map);
      } catch (e) {
        if (!cancelled) setBootError(e?.message || 'Could not load lessons.');
      }
    })();
    return () => { cancelled = true; };
  }, [learner?.id, trackId]);

  // ── Eligibility helpers ──────────────────────────────────────
  // Disable a tier when ANY required lesson is missing, already
  // completed, or marked test_out_ineligible. Each disabled tier
  // shows a short inline reason; we never throw an error.
  const lessonStatus = (lesson) => {
    if (!lesson) return { eligible: false, reason: 'End of track' };
    const row = progressByLid[lesson.id];
    if (row?.status === 'completed') return { eligible: false, reason: 'Already completed' };
    if (row?.mastery_level === TEST_OUT_INELIGIBLE) return { eligible: false, reason: 'Fast Track used' };
    return { eligible: true, reason: null };
  };

  const optionEligibility = (opt) => {
    const slice = lessons.slice(0, opt.lessonCount);
    if (slice.some(l => !l)) return { eligible: false, reason: 'Not enough lessons remaining' };
    const blockers = slice
      .map(l => lessonStatus(l))
      .filter(s => !s.eligible);
    if (blockers.length) return { eligible: false, reason: blockers[0].reason };
    return { eligible: true, reason: null };
  };

  const selectedOption  = TIERS.find(o => o.key === selectedOpt) || null;
  const selectedLessons = selectedOption ? lessons.slice(0, selectedOption.lessonCount).filter(Boolean) : [];

  // Default-select the first eligible tier once lessons are loaded so the
  // CTA is always actionable.
  useEffect(() => {
    if (selectedOpt || lessons.length === 0) return;
    const firstEligible = TIERS.find(o => optionEligibility(o).eligible);
    if (firstEligible) setSelectedOpt(firstEligible.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessons, progressByLid]);

  // ── Start Test Out → AI call → parse → render QuizPanel ──────
  // Generation behavior is unchanged: same callAI directive, same
  // payload shape, same one-retry-then-unavailable fallback.
  const startTestOut = async () => {
    if (!selectedOption || selectedLessons.length === 0) return;
    setQuizError(null);
    setQuizLoading(true);
    try {
      const basePayload = await buildStatePayload(learner.id, trackId, selectedLessons[0].id);
      // Inject the test_out_context the directive reads.
      const statePayload = {
        ...basePayload,
        test_out_context: {
          questionCount: selectedOption.questionCount,
          lessons: selectedLessons.map(l => ({
            id:         l.id,
            title:      l.title,
            objectives: Array.isArray(l.objectives) ? l.objectives : [],
          })),
        },
      };

      const buildCall = () => callAI({
        trackId,
        conversationHistory: [],
        userInput: `Generate the Test Out checkpoint quiz for: ${selectedLessons.map(l => l.title).join(' + ')}.`,
        statePayload,
        lessonData: selectedLessons[0],
        learnerId: learner.id,
        sessionPhase:  'CHECKPOINT',
        turnDirective: 'TEST_OUT_CHECKPOINT',
      });

      let parsed = null;
      try {
        const res = await buildCall();
        parsed = parseResponse(res?.content || '');
      } catch (e) { /* fall through to retry */ }
      if (!parsed?.quiz?.questions?.length) {
        // One retry per spec.
        try {
          const res2 = await buildCall();
          parsed = parseResponse(res2?.content || '');
        } catch (e) { /* fall through to fallback */ }
      }
      if (!parsed?.quiz?.questions?.length) {
        setQuizLoading(false);
        setPhase('unavailable');
        return;
      }
      setQuiz(parsed.quiz);
      setPhase('quiz');
    } catch (e) {
      setPhase('unavailable');
    } finally {
      setQuizLoading(false);
    }
  };

  // ── QuizPanel onComplete: pass/fail persistence (UNCHANGED) ───
  const handleQuizDone = async (correct, total /* , answers */) => {
    if (persistingRef.current) return;
    persistingRef.current = true;
    const accuracy = total > 0 ? correct / total : 0;
    const passed   = accuracy >= PASS_THRESHOLD;
    const slice    = selectedLessons; // already filtered + in sequence_order

    if (passed) {
      try {
        // Mark each selected lesson complete IN sequence_order. logQuizAttempt
        // tagged remediation_strategy='test_out_passed' is our source-of-record
        // for "completion came from Test Out" (no schema change).
        for (const l of slice) {
          await markLessonComplete(
            learner.id, l.id, trackId, l.module_id, accuracy,
            {
              mastery_score: accuracy,
              mastery_level: accuracy >= 0.85 ? 'mastered' : 'understood',
            },
          );
          await logQuizAttempt({
            learner_id:           learner.id,
            lesson_id:            l.id,
            attempt_number:       1,
            score:                accuracy,
            failed_concepts:      [],
            remediation_strategy: 'test_out_passed',
            checkpoint_type:      'test_out',
            video_triggered:      false,
            created_at:           new Date().toISOString(),
          });
        }
        // Advance current_lesson_id past the selected range.
        const lastSelected = slice[slice.length - 1];
        let advanceTo = lastSelected.id;
        try {
          const after = await getNextLesson(lastSelected.id);
          if (after?.id) advanceTo = after.id;
        } catch { /* end-of-track — stay on last selected */ }
        await upsertLearnerState(learner.id, trackId, {
          current_lesson_id:      advanceTo,
          last_checkpoint_result: 'pass',
          consecutive_failures:   0,
        });
      } catch (e) {
        console.error('TestOutTool pass persist:', e?.message);
      }
      setQuiz(null);
      setPhase('passed');
    } else {
      try {
        for (const l of slice) {
          // status stays 'in_progress' so existing readers continue to treat
          // the lesson as unfinished — guided path still works. mastery_level
          // is the retry gate this tool reads on next load.
          await upsertLessonProgress(learner.id, l.id, trackId, l.module_id, {
            mastery_score: accuracy,
            mastery_level: TEST_OUT_INELIGIBLE,
            status:        'in_progress',
          });
          await logQuizAttempt({
            learner_id:           learner.id,
            lesson_id:            l.id,
            attempt_number:       1,
            score:                accuracy,
            failed_concepts:      [],
            remediation_strategy: 'test_out_failed',
            checkpoint_type:      'test_out',
            video_triggered:      false,
            created_at:           new Date().toISOString(),
          });
        }
        // Do NOT clobber current_lesson_id — learner resumes via Study Console
        // at wherever the engine wants them. Just record the checkpoint result
        // for the per-track learner_state (consecutive_failures stays as-is).
        await upsertLearnerState(learner.id, trackId, {
          last_checkpoint_result: 'fail',
        });
      } catch (e) {
        console.error('TestOutTool fail persist:', e?.message);
      }
      setQuiz(null);
      setPhase('failed');
    }
    persistingRef.current = false;
  };

  // ── Navigation helpers ───────────────────────────────────────
  const goToStudyConsole = () => {
    if (typeof onSelectTrack !== 'function' || !trackId) return;
    onSelectTrack({ id: trackId, ...(track || {}) });
  };

  // ─────────────────────────────────────────────────────────────
  // Lesson context
  // ─────────────────────────────────────────────────────────────
  const currentLesson = lessons[0] || null;
  const lessonNumber  = currentLesson?.sequence_order != null ? currentLesson.sequence_order : null;
  const moduleTitle   = currentLesson?.modules?.title || null;

  // ─────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────
  const heading = { fontSize: 26, fontWeight: 800, color: '#1e1e2e', margin: '0 0 6px', letterSpacing: -0.3 };
  const sub     = { fontSize: 14, color: 'var(--brand-slate)', margin: 0, lineHeight: 1.55, maxWidth: 580 };

  if (bootError) {
    return (
      <div>
        <div style={{ marginBottom: 18 }}>
          <h2 style={heading}>Fast Track Assessment</h2>
          <p style={sub}>Think you already know this material? Take a mastery check.</p>
        </div>
        <div style={{ ...cardWhite, textAlign: 'center', padding: 28 }}>
          <p style={{ ...sub, margin: '0 auto' }}>{bootError}</p>
        </div>
      </div>
    );
  }

  if (lessons.length === 0) {
    return (
      <div>
        <h2 style={heading}>Fast Track Assessment</h2>
        <p style={{ ...sub, marginTop: 12 }}>Loading your lessons…</p>
      </div>
    );
  }

  if (phase === 'quiz' && quiz) {
    return (
      <div>
        <div style={{ marginBottom: 18 }}>
          <h2 style={heading}>Fast Track — In Progress</h2>
          <p style={sub}>{selectedLessons.map(l => l.title).join(' + ')}</p>
        </div>
        <QuizPanel quiz={quiz} onComplete={handleQuizDone} phase="CHECKPOINT" />
      </div>
    );
  }

  if (phase === 'passed') {
    return (
      <div>
        <div style={{ marginBottom: 18 }}>
          <h2 style={heading}>Fast Track Cleared</h2>
        </div>
        <div style={{ ...cardWhite, padding: 26, borderLeft: '4px solid var(--brand-emerald)' }}>
          <p style={{ fontSize: 15, color: '#1e1e2e', margin: '0 0 16px', fontWeight: 600, lineHeight: 1.5 }}>
            {COPY.pass}
          </p>
          <button type="button" onClick={goToStudyConsole} style={btnPrimary}>
            Continue at Next Lesson →
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'failed') {
    return (
      <div>
        <div style={{ marginBottom: 18 }}>
          <h2 style={heading}>Routing to Guided Path</h2>
        </div>
        <div style={{ ...cardWhite, padding: 26, borderLeft: '4px solid var(--brand-coral)' }}>
          <p style={{ fontSize: 15, color: '#1e1e2e', margin: '0 0 16px', fontWeight: 600, lineHeight: 1.5 }}>
            {COPY.fail}
          </p>
          <button type="button" onClick={goToStudyConsole} style={btnPrimary}>
            Open Study Console →
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'unavailable') {
    return (
      <div>
        <div style={{ marginBottom: 18 }}>
          <h2 style={heading}>Fast Track Assessment</h2>
        </div>
        <div style={{ ...cardWhite, padding: 26 }}>
          <p style={{ ...sub, marginBottom: 16 }}>{COPY.unavailable}</p>
          <button type="button" onClick={goToStudyConsole} style={btnPrimary}>
            Open Study Console →
          </button>
        </div>
      </div>
    );
  }

  // ── default: the Test Out selection page ─────────────────────
  const selectedEligible = selectedOption && optionEligibility(selectedOption).eligible;
  const lessonCtx = [track?.label, lessonNumber != null ? `Lesson ${lessonNumber}` : null]
    .filter(Boolean).join('  ·  ');

  return (
    <div style={{ maxWidth: 1100 }}>
      {/* Header + subcopy + lesson context */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={heading}>Fast Track Assessment</h2>
        <p style={sub}>
          Think you already know this material? Take a mastery check. Pass the gate and AdaptOS will move you forward.
        </p>
        {lessonCtx && (
          <p style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--brand-slate)', margin: '10px 0 0' }}>
            {lessonCtx}{moduleTitle ? `  ·  ${moduleTitle}` : ''}
          </p>
        )}
      </div>

      {/* How Fast Track Assessment works */}
      <div style={{ ...cardWhite, marginBottom: 20, padding: '16px 22px' }}>
        <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: 'var(--brand-slate)', margin: '0 0 12px' }}>
          How Fast Track Assessment works
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'center' }}>
          <HowItem tint="var(--brand-emerald)" icon={<IcCheck />}   title="80% required to advance" />
          <HowDivider />
          <HowItem tint="var(--brand-amber)"   icon={<IcClipboard />} title="Formal checkpoint — counts toward mastery" />
          <HowDivider />
          <HowItem tint="var(--brand-coral)"   icon={<IcRepair />}   title="Missed areas become repair targets" />
        </div>
      </div>

      {/* Tiers + diagnostic */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
        {/* Tier cards */}
        <div id="hint-to-selector" style={{ flex: '2 1 380px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {TIERS.map(opt => {
            const e = optionEligibility(opt);
            const active = selectedOpt === opt.key;
            const Icon = opt.Icon;
            return (
              <button
                key={opt.key}
                type="button"
                disabled={!e.eligible}
                onClick={() => e.eligible && setSelectedOpt(opt.key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 16, textAlign: 'left',
                  padding: '16px 18px', borderRadius: 14,
                  border: `2px solid ${active ? 'var(--brand-amber)' : '#e5e7eb'}`,
                  background: active ? '#fffaf0' : '#ffffff',
                  boxShadow: active ? '0 4px 16px rgba(217,119,6,0.14)' : 'var(--card-shadow)',
                  cursor: e.eligible ? 'pointer' : 'default',
                  opacity: e.eligible ? 1 : 0.55,
                  fontFamily: 'inherit', transition: 'all 0.15s',
                }}
              >
                <span style={{
                  width: 42, height: 42, borderRadius: 11, flexShrink: 0,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  color: opt.tint, background: tintWash(opt.tint),
                }}>
                  <Icon />
                </span>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 16.5, fontWeight: 800, color: '#1e1e2e' }}>{opt.name}</span>
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: 'var(--brand-slate)',
                      background: '#f1f3f7', borderRadius: 99, padding: '3px 10px', whiteSpace: 'nowrap',
                    }}>
                      {opt.questionCount} questions
                    </span>
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--brand-slate)', margin: '5px 0 0', lineHeight: 1.4 }}>
                    {opt.best}
                  </p>
                  <p style={{ fontSize: 11.5, fontWeight: 700, color: active ? 'var(--brand-amber)' : '#9aa3b2', margin: '6px 0 0', letterSpacing: 0.2 }}>
                    {opt.scope}
                  </p>
                  {!e.eligible && (
                    <p style={{ fontSize: 11.5, color: 'var(--brand-coral)', margin: '6px 0 0', fontWeight: 600 }}>
                      Unavailable — {e.reason}
                    </p>
                  )}
                </div>

                {/* radio indicator */}
                <span style={{
                  width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                  border: `2px solid ${active ? 'var(--brand-amber)' : '#cbd2dc'}`,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {active && <span style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--brand-amber)' }} />}
                </span>
              </button>
            );
          })}

          {/* Start CTA — under the tier cards, same width, slimmer */}
          <button
            type="button"
            id="hint-to-start"
            onClick={startTestOut}
            disabled={!selectedEligible || quizLoading}
            style={{
              ...btnPrimary, width: '100%', justifyContent: 'center', display: 'flex',
              padding: '12px 26px', marginTop: 4,
              opacity: (!selectedEligible || quizLoading) ? 0.55 : 1,
              cursor: (!selectedEligible || quizLoading) ? 'default' : 'pointer',
            }}
          >
            {quizLoading
              ? 'Preparing checkpoint…'
              : selectedOption ? `Start ${selectedOption.name} →` : 'Select a tier'}
          </button>
        </div>

        {/* Dark diagnostic panel */}
        <div id="hint-to-diagnostic" style={{
          flex: '1 1 300px', alignSelf: 'stretch',
          background: 'var(--surface-dark)', borderRadius: 16, padding: '20px 22px',
          color: '#e8e6e1', boxShadow: '0 6px 24px rgba(15,23,42,0.18)',
        }}>
          <p style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, color: '#fff', margin: '0 0 6px' }}>
            Diagnostic
          </p>

          <DiagRow label="Current focus">
            <span style={{ color: '#fff', fontWeight: 600 }}>{currentLesson?.title || '—'}</span>
          </DiagRow>

          <DiagRow label="Recent weak signal">
            {weakSignal?.errorType ? (
              <span style={{ color: 'var(--surface-amber-cream)', fontWeight: 600 }}>{prettySignal(weakSignal.errorType)}</span>
            ) : weakSignal?.lastResult === 'fail' ? (
              <span style={{ color: 'var(--surface-amber-cream)', fontWeight: 600 }}>Last checkpoint missed</span>
            ) : (
              <span style={{ color: '#94a3b8' }}>No recent gaps flagged</span>
            )}
          </DiagRow>

          <DiagRow label="Mastery gate">
            <span style={{ color: 'var(--brand-emerald)', fontWeight: 800, fontSize: 17 }}>80%</span>
            <span style={{ color: '#94a3b8', fontSize: 12 }}> required</span>
          </DiagRow>

          <DiagRow label="If you pass">
            <span style={{ color: '#86efac', fontWeight: 600 }}>Advance unlocked</span>
          </DiagRow>

          <DiagRow label="If you miss" last={readiness == null}>
            <span style={{ color: '#fca5a5', fontWeight: 600 }}>Repair path generated</span>
          </DiagRow>

          {readiness != null && (
            <div style={{ paddingTop: 14 }}>
              <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: '#94a3b8', margin: '0 0 8px' }}>
                Your readiness
              </p>
              <div style={{ position: 'relative', height: 8, borderRadius: 99, background: 'linear-gradient(90deg, var(--brand-coral) 0%, var(--brand-amber) 50%, var(--brand-emerald) 100%)' }}>
                <span style={{
                  position: 'absolute', top: -3, left: `calc(${Math.max(2, Math.min(98, readiness.pct || 0))}% - 7px)`,
                  width: 14, height: 14, borderRadius: '50%', background: '#fff', border: '2px solid var(--surface-dark)',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
                }} />
              </div>
              <p style={{ fontSize: 12, color: '#cbd5e1', margin: '10px 0 0', lineHeight: 1.45 }}>
                {Math.round(readiness.pct || 0)}% ready — a quick check fills the remaining gaps.
              </p>
            </div>
          )}
        </div>
      </div>

      {quizError && (
        <p style={{ fontSize: 12.5, color: 'var(--brand-coral)', margin: '12px 0 0' }}>
          {quizError}
        </p>
      )}
    </div>
  );
}

// ── Small presentational helpers ───────────────────────────────
function IcCheck()     { return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>); }
function IcClipboard() { return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="8" y="2" width="8" height="4" rx="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><path d="m9 14 2 2 4-4"/></svg>); }
function IcRepair()    { return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/><path d="M3 21v-5h5"/></svg>); }

function HowItem({ tint, icon, title }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
      <span style={{
        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: tint, background: tintWash(tint),
      }}>
        {icon}
      </span>
      <span style={{ fontSize: 13, fontWeight: 700, color: '#1e1e2e', lineHeight: 1.3 }}>{title}</span>
    </div>
  );
}

function HowDivider() {
  return <span style={{ width: 1, height: 22, background: '#e5e7eb', flexShrink: 0 }} aria-hidden />;
}

function DiagRow({ label, children, last }) {
  return (
    <div style={{ padding: '11px 0', borderBottom: last ? 'none' : '1px solid rgba(255,255,255,0.08)' }}>
      <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: '#94a3b8', margin: '0 0 5px' }}>
        {label}
      </p>
      <div style={{ fontSize: 13, lineHeight: 1.5 }}>{children}</div>
    </div>
  );
}

function prettySignal(errorType) {
  const s = String(errorType).replace(/[_-]+/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Translate a brand color into a faint wash for icon chips.
function tintWash(tint) {
  if (tint === 'var(--brand-emerald)') return 'rgba(5,150,105,0.12)';
  if (tint === 'var(--brand-amber)')   return 'rgba(217,119,6,0.12)';
  if (tint === 'var(--brand-coral)')   return 'rgba(220,38,38,0.10)';
  return 'rgba(124,58,237,0.12)';
}

const cardWhite = {
  background: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: 16,
  padding: '20px 22px',
  boxShadow: 'var(--card-shadow)',
};

const btnPrimary = {
  padding: '14px 26px',
  borderRadius: 12,
  background: 'var(--brand-amber)',
  border: 'none',
  color: '#ffffff',
  fontSize: 15,
  fontWeight: 800,
  fontFamily: 'inherit',
  cursor: 'pointer',
  letterSpacing: 0.2,
  boxShadow: '0 4px 14px rgba(217,119,6,0.28)',
};
